import FetchClient from "./FetchClient";
import { getTokensFromStorage } from "./TokenService";

/**
 * Email last-touch attribution. Marketing email links carry `?es=<sendId>`;
 * we keep the latest one for 30 days and, once the visitor is signed in, post
 * it to /email/touch once per value. The server only accepts sends addressed
 * to the signed-in account, so forwarded links are ignored. Fire-and-forget:
 * nothing here may throw into the UI.
 */

export const EMAIL_TOUCH_STORAGE_KEY = "dh_es";
export const EMAIL_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SEND_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export interface StoredEmailTouch {
  sendId: string;
  at: number;
  reported?: boolean;
}

function readStored(now: number): StoredEmailTouch | null {
  try {
    const raw = localStorage.getItem(EMAIL_TOUCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredEmailTouch>;
    if (typeof parsed?.sendId !== "string" || typeof parsed.at !== "number") return null;
    if (now - parsed.at > EMAIL_TOUCH_TTL_MS) {
      localStorage.removeItem(EMAIL_TOUCH_STORAGE_KEY);
      return null;
    }
    return { sendId: parsed.sendId, at: parsed.at, reported: parsed.reported === true };
  } catch {
    return null;
  }
}

function writeStored(touch: StoredEmailTouch) {
  try {
    localStorage.setItem(EMAIL_TOUCH_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    /* storage unavailable: attribution is best-effort */
  }
}

/** Read `es` from the landing URL (call once on app load, before routing). */
export function captureEmailTouch(input?: { search?: string; now?: number }): StoredEmailTouch | null {
  try {
    const search = input?.search ?? window.location.search;
    const now = input?.now ?? Date.now();
    const sendId = (new URLSearchParams(search).get("es") || "").trim();
    const existing = readStored(now);
    if (!sendId || !SEND_ID_PATTERN.test(sendId)) return existing;
    if (existing && existing.sendId === sendId) {
      // Same email clicked again: refresh the window, keep the reported flag.
      const refreshed = { ...existing, at: now };
      writeStored(refreshed);
      return refreshed;
    }
    const touch = { sendId, at: now, reported: false };
    writeStored(touch);
    return touch;
  } catch {
    return null;
  }
}

/** Post the stored touch for the signed-in account (once per send ID). */
export function reportEmailTouch(now: number = Date.now()): void {
  try {
    if (!getTokensFromStorage()?.accessToken) return;
    const touch = readStored(now);
    if (!touch || touch.reported) return;
    writeStored({ ...touch, reported: true });
    FetchClient.post("/email/touch", { sendId: touch.sendId }).catch(() => {
      const current = readStored(Date.now());
      if (current && current.sendId === touch.sendId) writeStored({ ...current, reported: false });
    });
  } catch {
    /* Attribution must never interrupt sign-in. */
  }
}
